import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks";
import styles from "../../styles/Spreadsheet/Spreadsheet.module.css";
import { formatDateUA, formatTimeOnly } from "../../helpers/formateDate";
import { docsFilter, PAGE_SET } from "../../actions/filter";
import {
  UPLOAD_RESET,
  UPLOAD_START_BATCH,
  uploadDocs,
} from "../../actions/upload";
import { getTypes } from "../../actions/types";
import { TdLoader } from "../Loaders/TableLoader";
import { SpreadsheetSkeletonRows } from "../Loaders/SkeletonLoader";
import { nameCompiler, nameLastCompiler } from "../../helpers/nameCompiler";
import { Counterparties } from "../View/Counterparties";
import { isEllipsisActive } from "../../helpers/isEllipsisActive";
import { executeScroll } from "../../helpers/executeScroll";
import { HtmlTooltip, LightTooltip } from "./ModTooltip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getDocStatus } from "../../helpers/docStatus";
import CircleLoader from "../Loaders/CircleLoader";
import PullToRefresh from "react-simple-pull-to-refresh";
import { SignStatus } from "../../styles/Spreadsheet/Spreadsheet.styled";
import {
  ProcessSigningIcon,
  EmptyDocForPdfViewer,
} from "../../../public/icons/index";
import {
  SpreadsheetFooter,
  SpreadsheetMobileFooter,
} from "./SpreadsheetFooter";

// function statusDocMob(doc, user) {
//   if (doc.cancelled) {
//     return (
//           <span>
//             <CanceledIcon />
//           </span>
//     )
//   } else if (doc.error) {
//       return doc.error.code === 115 ? (
//         <ProcessSigningIcon />
//       ) : (
//         <ErrorSignIcon />
//       );
//   }

//   const signature = doc.signatures.find((el) => !el.stamp);
//   // const stamp = doc.signatures.find((el) => el.stamp);
//   if (signature) {
//     return user.company.stamp && !stamp ? (
//       <React.Fragment>О.П.</React.Fragment>
//     ) : (
//       <React.Fragment>
//         {doc.counterparties.length &&
//         doc.counterparties.filter((el) => el.signatures.length).length ===
//           doc.counterparties.length ? (
//           <SignedByAllIcon />
//         ) : (
//           <SignedByYouIcon />
//         )}
//       </React.Fragment>
//     );
//   }
//   return (
//     <React.Fragment>
//       {user.company.stamp && !stamp ? <WaitingSignIcon /> : <WaitingSignIcon />}
//     </React.Fragment>
//   );
// }

const ROW = { display: "flex", alignItems: "center" } as const;

const StatusRow = ({
  status,
  title,
  tooltip,
}: {
  status?: string;
  title: string;
  tooltip?: React.ReactNode;
}) => {
  const inner = (
    <div style={ROW}>
      <SignStatus $status={status} />
      <span>{title}</span>
    </div>
  );
  return tooltip ? <LightTooltip title={tooltip}>{inner}</LightTooltip> : inner;
};

function DocStatusCell({ doc, user }: { doc: any; user: any }) {
  const { status, title } = getDocStatus(doc, user?.company);
  const stamp = doc.signatures?.find((el: any) => el.stamp);

  if (status === "cancelled") {
    return <StatusRow status="cancelled" title={title} tooltip={doc.comment} />;
  }

  if (status === "loading") {
    return (
      <div style={ROW}>
        <ProcessSigningIcon />
        <div>&nbsp;{title}</div>
      </div>
    );
  }

  if (status === "failure") {
    return (
      <StatusRow status="failure" title={title} tooltip={doc.error.message} />
    );
  }

  if (status === "readyToSign") {
    return (
      <div style={ROW}>
        <SignStatus $status="readyToSign" />
        <span>{title}</span>
      </div>
    );
  }

  if (status === "success" || status === "successAll") {
    if (user.company?.stamp && !stamp) {
      return <SignStatus>Очікує печатку</SignStatus>;
    }
    const signDate = (
      <>
        {formatDateUA(doc.signatures[0].date)}&nbsp;&nbsp;
        <span style={{ color: "var(--dark-gray)" }}>
          {formatTimeOnly(String(doc.signatures[0].date))}
        </span>
      </>
    );
    return <StatusRow status={status} title={title} tooltip={signDate} />;
  }

  if (user.company?.stamp && !stamp) {
    return (
      <StatusRow
        title="Очікує підпису та печатку"
        tooltip="Очікує підпису та печатку"
      />
    );
  }

  return <StatusRow title={title} />;
}

export const formatPrice = (amount: number) => {
  return new Intl.NumberFormat("uk-UA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export function MobileSpreadsheet(props: SpreadsheetProps) {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const observerTarget = useRef(null);
  const myRef = useRef(null);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [counterProps, setCounterProps] = useState([]);
  // const general = params?.type === "general";

  useEffect(() => {
    executeScroll(props, myRef);
  }, [props.docs.list.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && props.docs.more) {
          // props.setPage(props.page + 1);
          dispatch({ type: PAGE_SET });
        }
      },
      { threshold: 1 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [observerTarget, props.docs.list.length, props.docs.more]);

  const onHandleScroll = (e) => {
    props.setScrollPosition(e);
  };

  const handleClose = () => {
    setOpen(false);
    setCounterProps([]);
  };

  // const handleOpen = (list) => {
  //   setCounterProps(list);
  //   setOpen(true);
  // };
  const selected = props.docs.selected || {};
  const list = props.docs.list || [];

  const totalAmount = list.reduce((acc, doc) => {
    return selected[doc.id] ? acc + (Number(doc.amount) || 0) : acc;
  }, 0);

  const handleRefresh = () => {
    return new Promise((resolve) => {
      props.docsUpdate();
      resolve();
    });
  };

  if (props.docs.state === "success" && props.docs.list.length === 0)
    return (
      <div className={styles.noDocs}>
        <div style={{ textAlign: "center", width: "60%" }}>
          <EmptyDocForPdfViewer />
        </div>
      </div>
    );
  return (
    <div
      className={styles.wrapperMobi}
      id="scrollBox"
      onScroll={onHandleScroll}
      ref={myRef}
    >
      {props.docs.state === "loading" && props.filter.page === 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "60vh",
          }}
        >
          <CircleLoader />
        </div>
      )}

      <div
        className={
          props.heightTable
            ? `${styles.short_mobile_spreadsheet} ${styles.mobile_spreadsheet}`
            : styles.mobile_spreadsheet
        }
      >
        <PullToRefresh onRefresh={handleRefresh} pullingContent={""}>
          {props.docs.list?.map((doc, index) => (
            <div
              className={styles.mobile_spreadsheet_grid}
              key={doc.id}
              onClick={() => {
                navigate(props.link.replace("{id}", doc.id));
              }}
            >
              <label
                className={styles.check}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <input
                  type="checkbox"
                  // disabled={doc.cancelled}
                  onChange={(e) =>
                    props.toggleCheckbox(
                      doc.id,
                      e.target.checked,
                      index,
                      e.nativeEvent.shiftKey,
                    )
                  }
                  ref={(input) => {
                    if (input) {
                      input.checked = props.docs.selected
                        ? props.docs.selected[doc.id]
                        : false;
                    }
                  }}
                />
                <span
                  className={
                    // doc.cancelled ? styles.checkmarkCancelled : styles.checkmark
                    styles.checkmark
                  }
                ></span>
              </label>
              <div>
                <DocStatusCell doc={doc} user={user} />
              </div>
              <div style={{ color: "var(--login-gray)" }}>
                Дата завантаження:
              </div>
              <div style={{ color: "var(--login-gray)" }}>Дата:</div>
              <div>
                {formatDateUA(doc.date)}&nbsp;
                <span style={{ color: "var(--login-gray)" }}></span>
              </div>
              <div style={{ color: "var(--login-gray)" }}>Тип:</div>
              <div className={styles.docType}>{doc.type.title}</div>

              <div style={{ color: "var(--login-gray)" }}>Документ:</div>

              <div className={styles.docNamemobi}>
                <Link to={props.link.replace("{id}", doc.id)}>
                  {doc.title !== "" ? doc.title : doc.type.title}
                </Link>
              </div>
              <div style={{ color: "var(--login-gray)" }}>Сума:</div>
              <div className={styles.docType}>{formatPrice(doc.amount)}</div>
            </div>
          ))}

          {props.docs.state === "success" && (
            <div ref={observerTarget}>
              {props.docs.more ? <TdLoader /> : null}
            </div>
          )}
        </PullToRefresh>
        {Object.keys(props.docs.selected).length > 0 && (
          <SpreadsheetMobileFooter
            selectedDocs={props.docs.selected}
            allDocs={props.docs.list}
            totalAmount={totalAmount}
          />
        )}
      </div>
      {open && (
        <Counterparties
          open={open}
          handleClose={handleClose}
          counterparties={counterProps}
          pub={true}
          table={true}
        />
      )}
    </div>
  );
}

const readFileAsBase64 = (
  file: File,
): Promise<{ file: string; name: string }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      resolve({ file: base64, name: file.name });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export function Spreadsheet(props: SpreadsheetProps) {
  const user = useAppSelector((state) => state.user);
  const upload = useAppSelector((state) => state.upload);
  const filter = useAppSelector((state) => state.filter);
  const types = useAppSelector((state) => state.types);
  const params = useParams();
  const dispatch = useAppDispatch();
  const observerTarget = useRef(null);
  const myRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (types.state === "idle") dispatch(getTypes());
  }, [types.state]);

  useEffect(() => {
    if (upload.state === "success" && upload.total === 1) {
      navigate(`/documents/${upload.id}/view`);
      dispatch(docsFilter(filter.scope));
      dispatch({ type: UPLOAD_RESET });
    }
    if (
      upload.state === "success" &&
      upload.current === upload.total &&
      upload.total > 1
    ) {
      dispatch(docsFilter(filter.scope));
      dispatch({ type: UPLOAD_RESET });
    }
  }, [upload.state, upload.current]);

  const defaultTypeId =
    types.state === "success"
      ? (types.list.find((t: any) => t.title === "")?.id ?? "")
      : "";

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) return;
      dispatch({
        type: UPLOAD_START_BATCH,
        payload: { total: acceptedFiles.length },
      });
      if (acceptedFiles.length > 1) navigate("/documents/all");
      for (const file of acceptedFiles) {
        const fileData = await readFileAsBase64(file);
        await dispatch(
          uploadDocs({
            file: fileData.file,
            name: fileData.name,
            type: defaultTypeId,
          }) as any,
        );
      }
    },
    [defaultTypeId, dispatch, navigate],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    noClick: true,
  });
  const general = params?.type === "general";
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyCode = (e: React.MouseEvent, text: string, id: string) => {
    e.stopPropagation();
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text);
    } else {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const selected = props.docs.selected || {};
  const list = props.docs.list || [];

  const totalAmount = list.reduce((acc, doc) => {
    return selected[doc.id] ? acc + (Number(doc.amount) || 0) : acc;
  }, 0);

  const isLoading = props.docs.state === "loading" && props.filter.page === 0;

  useEffect(() => {
    executeScroll(props, myRef);
  }, [props.docs.list.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && props.docs.more) {
          dispatch({ type: PAGE_SET });
        }
      },
      { threshold: 0 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [observerTarget, props.docs.list.length, props.docs.more]);

  if (props.docs.state === "success" && props.docs.list.length === 0)
    return (
      <div className={styles.noDocs}>
        <div style={{ textAlign: "center" }}>
          <EmptyDocForPdfViewer />
        </div>
      </div>
    );

  return (
    <>
      <div {...getRootProps()} style={{ position: "relative" }}>
        <input {...getInputProps()} />
        {isDragActive && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              background: "rgba(59,130,246,0.12)",
              border: "1px dashed var(--blue)",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          ></div>
        )}
        <div
          className={`${
            !general ? styles.wrapperScroll : styles.generalWrapperScroll
          }`}
          id="scrollBox"
          onScroll={props.setScrollPosition}
          ref={myRef}
        >
          <table className={`${styles.desktop_spreadsheet}`} id="data-table">
            <thead>
              {props.docs.list.length || !isLoading ? (
                <tr>
                  <th>
                    <label className={styles.check}>
                      <input
                        type="checkbox"
                        onChange={(e) => props.toggleAll(e.target.checked)}
                        ref={(input) => {
                          if (input) {
                            const selected = Object.values(
                              props.docs.selected || {},
                            ).filter((el) => !!el).length;
                            const active = props.docs.list.filter(
                              (el) => !el.cancelled,
                            ).length;
                            if (selected > 0) {
                              input.indeterminate = selected !== active;
                              input.checked = selected === active;
                            } else {
                              input.checked = input.indeterminate = false;
                            }
                          }
                        }}
                      />
                      <span className={styles.checkmark}></span>
                    </label>
                  </th>
                  <th>
                    <p className={styles.docTime}>Дата</p>
                  </th>
                  <th>Номер</th>
                  <th>Тип</th>
                  <th>Документ</th>
                  <th>Сума</th>
                  <th>Статус</th>
                  <th style={{ color: "var(--login-gray)" }}>
                    Дата завантаження
                  </th>
                  <th>Компанія</th>
                  <th>ЄДРПОУ/ІПН</th>
                  <th>Контрагент</th>
                </tr>
              ) : (
                <tr>
                  <th
                    colSpan={11}
                    style={{ textAlign: "center", width: "100%" }}
                  ></th>
                </tr>
              )}
            </thead>
            {props.docs.list.length || !isLoading ? (
              <>
                <tbody>
                  {props.docs.list?.map((doc, index) => (
                    <tr
                      className={styles.tableRow}
                      key={doc.id}
                      onClick={() => {
                        navigate(props.link.replace("{id}", doc.id), {
                          state: !general,
                        });
                      }}
                    >
                      <td
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <label className={styles.check}>
                          <input
                            type="checkbox"
                            // disabled={doc.cancelled}
                            onChange={(e) => {
                              props.toggleCheckbox(
                                doc.id,
                                e.target.checked,
                                index,
                                e.nativeEvent.shiftKey,
                              );
                            }}
                            ref={(input) => {
                              if (input) {
                                input.checked = props.docs.selected
                                  ? props.docs.selected[doc.id]
                                  : false;
                              }
                            }}
                          />
                          <span
                            className={
                              // doc.cancelled ? styles.checkmarkCancelled : styles.checkmark
                              styles.checkmark
                            }
                          ></span>
                        </label>
                      </td>
                      <td>
                        <p className={styles.docTime}>
                          {formatDateUA(doc.date)}&nbsp;
                          <span
                            style={{ color: "var(--disabled-btn-bg)" }}
                          ></span>
                        </p>
                      </td>
                      <td>{doc.number}</td>
                      <td>
                        <LightTooltip
                          title={doc.type.title}
                          disableHoverListener={
                            !isEllipsisActive("type" + doc.id)
                          }
                        >
                          <p id={"type" + doc.id} className={styles.docType}>
                            {doc.type.title}
                          </p>
                        </LightTooltip>
                      </td>

                      <td>
                        <LightTooltip
                          title={doc.title}
                          disableHoverListener={!isEllipsisActive(doc.id)}
                        >
                          <p id={doc.id} className={styles.docName}>
                            {doc.title !== "" ? doc.title : doc.type.title}{" "}
                          </p>
                        </LightTooltip>
                      </td>
                      <td>
                        <div className={styles.docStatus}>
                          {formatPrice(doc.amount)}
                        </div>
                      </td>
                      <td>
                        <div className={styles.docStatus}>
                          <DocStatusCell doc={doc} user={user} />
                        </div>
                      </td>
                                            <td>
                        <p className={styles.docTime}>
                          {formatDateUA(doc.createdAt)}&nbsp;
                          <span style={{ color: "var(--disabled-btn-bg)", whiteSpace: "nowrap" }}>
                            {formatTimeOnly(String(doc.createdAt))}
                          </span>
                        </p>
                      </td>

                      <td>
                        <p className={styles.docCompany}>
                          {doc.counterparties[0]?.company.title}
                        </p>
                      </td>
                      <td>
                        {doc.counterparties && doc.counterparties.length > 0 ? (
                          <span className={styles.codeCell}>
                            {doc.counterparties[0]?.company.code.length !== 0
                              ? doc.counterparties[0].company.code
                              : doc.counterparties[0].user.code}
                            <LightTooltip title={copiedId === doc.id ? "Скопійовано" : "Скопіювати"}>
                              <ContentCopyIcon
                                className={styles.copyIcon}
                                sx={{ fontSize: 13, cursor: "pointer", color: "var(--disabled-c)" }}
                                onClick={(e) => {
                                  const code = doc.counterparties[0]?.company.code.length !== 0
                                    ? doc.counterparties[0].company.code
                                    : doc.counterparties[0].user.code;
                                  handleCopyCode(e, code, doc.id);
                                }}
                              />
                            </LightTooltip>
                          </span>
                        ) : null}
                      </td>

                      <td
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        {doc.counterparties &&
                          doc.counterparties.length > 0 && (
                            <p
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                width: "100%",
                              }}
                            >
                              {nameLastCompiler(doc.counterparties[0].user)}
                            </p>
                          )}
                        {doc.counterparties &&
                          doc.counterparties.length > 1 && (
                            <HtmlTooltip
                              title={
                                <React.Fragment>
                                  <ul>
                                    {doc.counterparties
                                      .slice(1)
                                      .map((i, index) => (
                                        <li key={index}>
                                          {i.user
                                            ? nameCompiler(i.user)
                                            : i.company?.title}
                                        </li>
                                      ))}
                                  </ul>
                                </React.Fragment>
                              }
                            >
                              <> </>
                            </HtmlTooltip>
                          )}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    {props.docs.state === "success" && (
                      <td colSpan={11} style={{ padding: 0 }}>
                        <div ref={observerTarget} style={{ marginTop: "-12px" }}>
                          {props.docs.more && <TdLoader />}
                        </div>
                      </td>
                    )}
                  </tr>
                </tbody>
                <SpreadsheetFooter
                  selectedDocs={props.docs.selected}
                  allDocs={props.docs.list}
                  totalAmount={totalAmount}
                />
              </>
            ) : (
              <tbody>
                <SpreadsheetSkeletonRows cols={11} />
              </tbody>
            )}
          </table>

          {props.docs.state === "failure" && (
            <div className={styles.noDocs}>
              <div style={{ width: "100%", textAlign: "center" }}>
                <p style={{ fontSize: "1.5rem" }}>Вибачте!</p>
                <br />
                <p>Здається, ми не можемо знайти ресурс, який ви шукаєте.</p>
                <br />
                <p>
                  Будь ласка, переконайтеся, що адреса веб-сайту написана
                  правильно.
                </p>
                <br />
                <p>Або скористайтеся меню, щоб перейти до певного розділу.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
